import React, { useEffect, useState } from "react";
import Button, { ButtonProps } from "../reusable/Button";
import { cn } from "@/lib/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { cva } from "class-variance-authority";
import ResponsiveImage from "../ui/ResponsiveImage";
import { usePathname } from "next/navigation";
import BreadCrumbs from "@/components/Gallery/BreadCrumbs";
import { getBlogUrl } from "@/lib/blog";
import { getHeroBreadcrumbSegments } from "./getHeroBreadcrumbSegments";

interface HeroProps {
  data: HeroData;
  pageData: any;
}

export interface HeroData {
  title: string;
  bottomTitle?: string;
  subtitle: string;
  long_subtitle?: string;
  image: any[];
  image_link: any[];
  buttons: Array<{
    id: string;
    item: ButtonProps;
  }>;
  box_alignment: "left" | "center" | "right";
}

const heroBoxVariants = cva(
  "relative md:absolute md:bottom-16 md:z-10 desktop-h-700-bottom-4 tablet-h-600-bottom-neg-8",
  {
  variants: {
    alignment: {
      left: [
        "w-full",
        "md:max-w-[90%] md:left-1/2 md:-translate-x-1/2",
        "desktop:max-w-[42.5vw] desktop:left-[128px] desktop:translate-x-0",
      ],
      center: [
        "md:max-w-[90%] w-full",
        "md:left-1/2 md:-translate-x-1/2",
        "desktop:max-w-[85vw] desktop:left-1/2 desktop:-translate-x-1/2",
      ],
      right: [
        "w-full",
        "md:max-w-[90%] md:left-1/2 md:-translate-x-1/2",
        // Desktop: right aligned, max width 50% of 85vw
        "desktop:max-w-[42.5vw] desktop:right-[128px] desktop:left-auto desktop:-translate-x-0",
      ],
    },
  },
  defaultVariants: {
    alignment: "center",
  },
});

export default function Hero({ data, pageData }: HeroProps) {
  const pathnameRaw = usePathname();
  const [pathname, setPathname] = useState<string | null>(null);
  useEffect(() => {
    setPathname(pathnameRaw);
  }, [pathnameRaw]);
  const isHomepage = pathname === "/" || pathname?.includes("/home");
  const animDuration = isHomepage ? 0.8 : 0.5;

  const useCloudinary = data.image_link.length;

  const desktopImage = useCloudinary
    ? data.image_link[0].item.desktop_link
    : data.image[0].item.desktop_image;
  const tabletImage = useCloudinary
    ? data.image_link[0].item.tablet_link
    : data.image[0].item.tablet_image;
  const mobileImage = useCloudinary
    ? data.image_link[0].item.mobile_link
    : data.image[0].item.mobile_image;
  const alt = useCloudinary
    ? data.image_link[0].item.alt ?? data.title
    : data.image[0].item.imageAlt ?? data.title;

  return (
    <LazyMotion features={domAnimation}>
      <section className="relative w-full">
        <div
          className={`relative w-full overflow-hidden h-[50vh] md:h-[100vh]`}
        >
          <ResponsiveImage
            desktop_image={desktopImage}
            tablet_image={tabletImage}
            mobile_image={mobileImage}
            alt={alt}
            priority
            className="z-0"
            shouldFetch={!useCloudinary}
          />
        </div>

        <div className={cn(heroBoxVariants({ alignment: data.box_alignment }))}>
          <m.div
            className="bg-accent-color-three p-12 text-center desktop-h-700-p-6 tablet-h-600-p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animDuration, ease: "easeOut" }}
          >
            <m.h1
              className={cn(
                "font-serif font-light tracking-normal text-center text-accent-color-two",
                "text-h1-mobile",
                "md:text-h1-tablet",
                "desktop:text-h1-desktop",
                "desktop-h-700-text-h1-tablet",
                "tablet-h-600-text-h1"
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: animDuration,
                ease: "easeOut",
                delay: 0.1,
              }}
            >
              {data.title}
            </m.h1>
            {data.bottomTitle && (
              <m.h2
                className={cn(
                  "font-sans font-normal text-center uppercase text-secondary",
                  "text-h1-mobile",
                  "md:text-special-h1-tablet",
                  "desktop:text-special-h1-desktop",
                  "desktop-h-700-text-xl",
                  "pt-6",
                  "desktop-h-700-pt-0",
                  "tablet-h-600-text-xl",
                  "tablet-h-600-pt-2"
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: animDuration,
                  ease: "easeOut",
                  delay: 0.1,
                }}
              >
                {data.bottomTitle}
              </m.h2>
            )}
            {data.long_subtitle ? (
              <m.div
                className="font-sans text-paragraph-large text-center text-white pt-6 pb-6 desktop-h-700-text-paragraph-medium desktop-h-700-pt-3 desktop-h-700-pb-3 tablet-h-600-text-paragraph tablet-h-600-pt-2 tablet-h-600-pb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: animDuration,
                  ease: "easeOut",
                  delay: 0.2,
                }}
                dangerouslySetInnerHTML={{ __html: data.long_subtitle }}
              ></m.div>
            ) : (
              <m.p
                className="font-sans text-paragraph-large text-center text-white pt-6 pb-6 desktop-h-700-text-paragraph-medium desktop-h-700-pt-3 desktop-h-700-pb-3 tablet-h-600-text-paragraph tablet-h-600-pt-2 tablet-h-600-pb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: animDuration,
                  ease: "easeOut",
                  delay: 0.2,
                }}
              >
                {data.subtitle}
              </m.p>
            )}
            <m.div
              className="flex flex-col gap-4 md:flex-row md:justify-center md:gap-x-4 desktop-h-700-gap-2 tablet-h-600-gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: animDuration,
                ease: "easeOut",
                delay: 0.3,
              }}
            >
              {data.buttons.map((button: any) => (
                <Button
                  type={button.item.type}
                  title={button.item.title}
                  link={button.item.link}
                  key={button.id}
                  className="leading-6 tracking-[0.15em] text-[18px] desktop-h-700-text-14px desktop-h-700-leading-5 tablet-h-600-text-13px tablet-h-600-leading-4"
                />
              ))}
            </m.div>
          </m.div>
        </div>
      </section>
      {pathname && !pathname?.includes(getBlogUrl()) && (
        <BreadCrumbs
          hideOnHomepage
          className="bg-accent-color-three-light text-[#fff] py-6 px-6 md:py-6 md:px-12 lg:py-8 lg:px-32 m-0 text-span-medium"
          segments={getHeroBreadcrumbSegments(pathname, pageData.title)}
        />
      )}
    </LazyMotion>
  );
}
